import {
  CommitmentPlanSortBy,
  CommitmentPlanSortDirection,
  InvestorDetailsLegalEntityAssetClassTargetsQuery,
  InvestorDetailsPageDetailsSectionLegalEntityQuery
} from '@/__generated__/graphql';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { InfoIcon } from '@/assets/icons/info-icons/info';
import { TerminatedIcon } from '@/assets/icons/investment-status/treminated';
import Button from '@/components/basicComponents/button';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { getInvestorEntityType } from '@/components/transactions-page/generateSignaturePacket-page/details';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { ContainerPage } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { TaxStatus } from '@trace-8/trace8-schema';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  EditInvestorAllocationSettingsAlwaysShowOptionEnum,
  EditInvestorEntityDetailsTaxStatusOptionEnum,
  EditInvestorModalPageTypeEnum
} from '../../editInvestorPage/types';
import {
  INVESTOR_DETAILS_COMMITMENT_PLANS_TOTAL_QUERY,
  INVESTOR_DETAILS_GROUP_ASSET_CLASS_TARGETS_QUERY,
  INVESTOR_DETAILS_LEGAL_ENTITY_ASSET_CLASS_TARGETS_QUERY
} from '../queries';
import { InvestorDetailsEntityTypeEnum, InvestorDetailsTabsEnum } from '../types';
import SimpleTable from './assetClassSimpleTable';
import NotificationWithProgressBar from './notificationWithProgressBar';
import {
  INVESTOR_DETAILS_PAGE_DETAILS_SECTION_GROUP_BY_PRIMARY_ENTITY_ID_QUERY,
  INVESTOR_DETAILS_PAGE_DETAILS_SECTION_LEGAL_ENTITY_QUERY
} from './queries';

const sortByName = <T extends { name: string }>(arr: T[]) => {
  return [...arr].sort((a, b) => a.name.localeCompare(b.name));
};

interface DetailsProps {
  entityType: InvestorDetailsEntityTypeEnum;
}

const Details = ({ entityType }: DetailsProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { searchParams, onChangeActiveTab } = useSearchParamsManager();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';
  const backToTitle = searchParams.get('backToTitle') || '';
  const activeTab: InvestorDetailsTabsEnum = (searchParams.get('activeTab') as InvestorDetailsTabsEnum) || InvestorDetailsTabsEnum.SUMMARY;
  const toggle = searchParams.get('toggle') === null || searchParams.get('toggle') === 'true';
  const isGroup = searchParams.get('isGroup') === 'true';

  const [detailsData, setDetailsData] = useState<InvestorDetailsPageDetailsSectionLegalEntityQuery['legalEntity']>(null);
  const [groupTaxStatus, setGroupTaxStatus] = useState('');
  const [targetOCRatio, setTargetOCRatio] = useState<number | null>(null);
  const [ignoreAllocationTargets, setIgnoreAllocationTargets] = useState(false);
  const [ignoreBiteSize, setIgnoreBiteSize] = useState(false);
  const [hasCommitmentPlans, setHasCommitmentPlans] = useState(false);
  const [assetClassTableData, setAssetClassTableData] = useState<
    InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets']
  >([]);

  const { loading: commitmentPlansTotalLoading } = useQuery(INVESTOR_DETAILS_COMMITMENT_PLANS_TOTAL_QUERY, {
    skip: !entityId,
    variables: {
      input: {
        legalEntityId: entityId,
        sortBy: CommitmentPlanSortBy.Name,
        sortDirection: CommitmentPlanSortDirection.Asc,
        limit: 1
      }
    },
    onCompleted: (data) => {
      const { total } = data.commitmentPlans;
      setHasCommitmentPlans(total > 0);
    }
  });

  const { loading: legalEntityAssetClassTargetsLoading } = useQuery(INVESTOR_DETAILS_LEGAL_ENTITY_ASSET_CLASS_TARGETS_QUERY, {
    skip: !entityId || !entityType || entityType === InvestorDetailsEntityTypeEnum.Group,
    variables: {
      id: entityId,
      assetClassTargetsInput: {
        includingProposed: toggle
      }
    },
    onCompleted: (data) => {
      const { targetOCRatio, ignoreBiteSize, ignoreAllocationTargets, assetClassTargets } = data.legalEntity;
      setTargetOCRatio(targetOCRatio);
      setIgnoreBiteSize(ignoreBiteSize);
      setIgnoreAllocationTargets(ignoreAllocationTargets);

      const sortedAssetClassTargets = sortByName(assetClassTargets);
      sortedAssetClassTargets.forEach((item) => {
        item.subAssetClasses = sortByName(item.subAssetClasses);
      });
      setAssetClassTableData(sortedAssetClassTargets);
    }
  });

  const { loading: groupAssetClassTargetsLoading } = useQuery(INVESTOR_DETAILS_GROUP_ASSET_CLASS_TARGETS_QUERY, {
    skip: !entityId || !entityType || entityType !== InvestorDetailsEntityTypeEnum.Group,
    variables: {
      data: {
        primaryEntityId: entityId,
        includeRecommendations: toggle
      },
      assetClassTargetsInput: {
        includingProposed: toggle
      }
    },
    onCompleted: (data) => {
      const { targetOCRatio, ignoreBiteSize, ignoreAllocationTargets, assetClassTargets } = data.group;
      setTargetOCRatio(targetOCRatio);
      setIgnoreBiteSize(ignoreBiteSize);
      setIgnoreAllocationTargets(ignoreAllocationTargets);

      const sortedAssetClassTargets = sortByName(assetClassTargets);
      sortedAssetClassTargets.forEach((item) => {
        item.subAssetClasses = sortByName(item.subAssetClasses);
      });
      setAssetClassTableData(sortedAssetClassTargets);
    }
  });

  const { loading: legalEntityDataLoading } = useQuery(INVESTOR_DETAILS_PAGE_DETAILS_SECTION_LEGAL_ENTITY_QUERY, {
    skip: !entityId || !entityType || entityType === InvestorDetailsEntityTypeEnum.Group,
    variables: {
      id: entityId,
      includingProposed: toggle
    },
    onCompleted: (data) => {
      const { __typename, ...legalEntityData } = data?.legalEntity;
      setDetailsData(legalEntityData || null);
    }
  });

  const { loading: groupDataLoading } = useQuery(INVESTOR_DETAILS_PAGE_DETAILS_SECTION_GROUP_BY_PRIMARY_ENTITY_ID_QUERY, {
    skip: !entityId || !entityType || entityType !== InvestorDetailsEntityTypeEnum.Group,
    variables: {
      data: {
        primaryEntityId: entityId,
        includeRecommendations: toggle
      }
    },
    onCompleted: (data) => {
      const { __typename, entities, ...groupData } = data?.group;

      const uniqueTaxStatuses = new Set(
        entities?.map((entity) => {
          return entity.taxStatus || (entity.taxExempt ? TaxStatus.US_TAX_EXEMPT : TaxStatus.US_TAXABLE);
        })
      );

      setGroupTaxStatus(uniqueTaxStatuses.size > 1 ? 'Mixed' : EditInvestorEntityDetailsTaxStatusOptionEnum[Array.from(uniqueTaxStatuses)?.[0]]);
      setDetailsData(groupData || null);
    }
  });

  const moreDetailsData: { name: string; showItem: boolean; value: JSX.Element }[] = useMemo(() => {
    if (!detailsData) {
      return [];
    }
    const { taxStatus } = detailsData;

    return [
      {
        name: 'Tax Status',
        showItem: true,
        value: (
          <>
            {entityType === InvestorDetailsEntityTypeEnum.Group && groupTaxStatus
              ? groupTaxStatus
              : EditInvestorEntityDetailsTaxStatusOptionEnum[taxStatus]}
          </>
        )
      },
      {
        name: 'Entity Type',
        showItem: true,
        value: (
          <>{entityType === InvestorDetailsEntityTypeEnum.Group ? 'N/A' : getInvestorEntityType(detailsData?.entityType, detailsData?.otherType)}</>
        )
      }
    ];
  }, [entityType, detailsData, ignoreBiteSize, groupTaxStatus]);

  const infoTagsData: { name: string; showItem: boolean; value: JSX.Element }[] = useMemo(() => {
    if (!detailsData) {
      return [];
    }
    const { defaultBiteSize1, defaultBiteSize2, alwaysShow } = detailsData;

    return [
      {
        name: 'Investor Review',
        showItem: entityType !== InvestorDetailsEntityTypeEnum.GroupedEntity && alwaysShow,
        value: <>{EditInvestorAllocationSettingsAlwaysShowOptionEnum.ShowAll}</>
      },
      {
        name: 'Bite Size (fund/direct)',
        showItem: entityType !== InvestorDetailsEntityTypeEnum.GroupedEntity && !ignoreBiteSize,
        value: (
          <BiteSizeValueWrapper>
            {defaultBiteSize1 && !isNaN(defaultBiteSize1) && defaultBiteSize1 > 0 ? (
              <FormattingTooltip zIndex={1000}>{+defaultBiteSize1}</FormattingTooltip>
            ) : (
              '$0'
            )}
            {' / '}
            {defaultBiteSize2 && !isNaN(defaultBiteSize2) && defaultBiteSize2 > 0 ? (
              <FormattingTooltip zIndex={1000}>{+defaultBiteSize2}</FormattingTooltip>
            ) : (
              '$0'
            )}
          </BiteSizeValueWrapper>
        )
      }
    ];
  }, [entityType, detailsData, ignoreBiteSize, groupTaxStatus]);

  const getNotificationDescription = (type: 'pacingModel' | 'overCommitmentRatio') => {
    const addFormattingTooltip = (amount: number) => <FormattingTooltip>{amount}</FormattingTooltip>;
    const { economicExposure, capitalTarget, totalCapitalTarget } = detailsData?.progressBarValues || {};
    const { totalCommitments, planGoal, totalGoal } = detailsData?.pacingProgressBarValues || {};

    const pacingModel = (
      <div>
        Showing Total Committed Capital for all commitments since plan start {addFormattingTooltip(totalCommitments)}, Pacing Pacing Plan Goal since
        start {addFormattingTooltip(planGoal)}, and Pacing Plan Plus Total Evergreen Target allocation {addFormattingTooltip(totalGoal)}.
      </div>
    );

    const overCommitmentRatio = (
      <div>
        Showing Current Economic Exposure {addFormattingTooltip(economicExposure)}, Target Private Capital without Over-Commitment Ratio{' '}
        {addFormattingTooltip(capitalTarget)}, Target Private Capital with Over-Commitment Ratio {addFormattingTooltip(totalCapitalTarget)}
      </div>
    );

    const notificationDescriptions = { pacingModel, overCommitmentRatio };

    return notificationDescriptions[type];
  };

  const openEditInvestorPage = () => {
    navigate(
      `/investors/${investorId}/${entityId}/edit?activeTab=${activeTab}&backToTitle=Investor Details&modalPageType=${EditInvestorModalPageTypeEnum.AllocationTargets}&isGroup=${isGroup}`,
      { state: { returnToInvestorDetails: true } }
    );
  };

  const openAutoPlanWizardPage = () => {
    navigate(
      `/investors/${investorId}/${entityId}?activeTab=${InvestorDetailsTabsEnum.PACING}&backToTitle=${backToTitle}&isGroup=${isGroup}&openAutoPlanWizard=true`,
      { replace: true }
    );
  };

  const hasInfoTags = infoTagsData?.some((item) => item.showItem);

  if (
    commitmentPlansTotalLoading ||
    legalEntityAssetClassTargetsLoading ||
    groupAssetClassTargetsLoading ||
    legalEntityDataLoading ||
    groupDataLoading
  ) {
    return <></>;
  }

  return (
    <DetailsWrap isMobile={isMobile}>
      {entityType === InvestorDetailsEntityTypeEnum.GroupedEntity && (
        <CardsContainer isMobile={isMobile}>
          <Card isMobile={isMobile}>
            <CardTitle>
              <FormattingTooltip zIndex={120}>{detailsData?.totalEntityValue || 0}</FormattingTooltip>
            </CardTitle>
            <CardDesc>TOTAL ENTITY VALUE</CardDesc>
          </Card>
          <Card isMobile={isMobile}>
            <CardTitle>
              <FormattingTooltip zIndex={120}>{detailsData?.currentAllocation || 0}</FormattingTooltip>
            </CardTitle>
            <CardDesc>CURRENT NAV</CardDesc>
          </Card>
          <Card isMobile={isMobile}>
            <CardTitle>
              <FormattingTooltip zIndex={120}>{detailsData?.unfundedCommitment || 0}</FormattingTooltip>
            </CardTitle>
            <CardDesc>UNFUNDED COMMITMENT</CardDesc>
          </Card>
        </CardsContainer>
      )}

      {entityType !== InvestorDetailsEntityTypeEnum.GroupedEntity && !ignoreAllocationTargets && (
        <CardsContainer isMobile={isMobile}>
          <Card isMobile={isMobile}>
            <CardTitle>
              <FormattingTooltip zIndex={120}>{detailsData?.totalEntityValue || 0}</FormattingTooltip>
            </CardTitle>
            <CardDesc>TOTAL ENTITY VALUE</CardDesc>
          </Card>
          <Card isMobile={isMobile}>
            <CardTitle>{detailsData?.privatePercentage?.toFixed(2) || 0}%</CardTitle>
            <CardDesc>PRIVATE CAPITAL TARGET</CardDesc>
          </Card>
          <>
            {targetOCRatio === null ? (
              <>
                <Card isMobile={isMobile}>
                  <CardTitle>
                    <FormattingTooltip zIndex={120}>{detailsData?.targetAllocation || 0}</FormattingTooltip>
                  </CardTitle>
                  <CardDesc>TARGET ALLOCATION</CardDesc>
                </Card>
                <Card isMobile={isMobile}>
                  <CardTitle>
                    <FormattingTooltip zIndex={120}>{detailsData?.currentAllocation || 0}</FormattingTooltip>
                  </CardTitle>
                  <CardDesc>CURRENT NAV</CardDesc>
                </Card>
              </>
            ) : (
              <>
                <Card isMobile={isMobile}>
                  <CardTitle>{detailsData?.targetOCRatio?.toFixed(2) || 0}x</CardTitle>
                  <CardDesc>TARGET OC RATIO</CardDesc>
                </Card>
                <Card isMobile={isMobile}>
                  <CardTitle>{detailsData?.currentOCRatio === -1 ? '-' : `${detailsData?.currentOCRatio?.toFixed(2) || 0}x`}</CardTitle>
                  <CardDesc>CURRENT OC RATIO</CardDesc>
                </Card>
              </>
            )}
          </>
        </CardsContainer>
      )}

      {activeTab === InvestorDetailsTabsEnum.SUMMARY &&
        entityType !== InvestorDetailsEntityTypeEnum.GroupedEntity &&
        !commitmentPlansTotalLoading && (
          <>
            {/* {(ignoreAllocationTargets || !hasCommitmentPlans) && targetOCRatio === null && ( */}
            {!ignoreAllocationTargets && !hasCommitmentPlans && targetOCRatio === null && (
              <NotificationWithProgressBar
                notificationTitle="Create a Commitment Plan"
                notificationDescription="Use our automated pacing model to create a commitment plan that achieves the investor's target allocation."
                progressBarData={null}
                customEditButton={
                  <Button onClick={openAutoPlanWizardPage} size="sm">
                    Create Commitment Plan
                  </Button>
                }
              />
            )}

            {/* {ignoreAllocationTargets && targetOCRatio !== null && ( */}
            {ignoreAllocationTargets && (
              <NotificationWithProgressBar
                notificationTitle="Enter Allocation Targets"
                notificationDescription="When you provide Allocation Targets for this entity capacity calculations and allocation criteria checks are made available."
                progressBarData={null}
                customEditButton={
                  <Button onClick={openEditInvestorPage} size="sm">
                    Enter Allocation Targets
                  </Button>
                }
              />
            )}
          </>
        )}

      {activeTab === InvestorDetailsTabsEnum.SUMMARY &&
        entityType !== InvestorDetailsEntityTypeEnum.GroupedEntity &&
        !ignoreAllocationTargets &&
        !commitmentPlansTotalLoading && (
          <>
            {hasCommitmentPlans && targetOCRatio === null && (
              <NotificationWithProgressBar
                notificationTitle="Commitment Plan To Date"
                notificationDescription={getNotificationDescription('pacingModel')}
                progressBarData={{
                  goal: detailsData?.pacingProgressBarValues?.planGoal || 0,
                  totalGoal: detailsData?.pacingProgressBarValues?.totalGoal || 0,
                  isGoalAchieved: false,
                  // isGoalAchieved: detailsData?.pacingProgressBarValues?.isGoalAchieved || false,
                  total: detailsData?.pacingProgressBarValues?.totalCommitments || 0
                }}
                onClickEditButton={() => {
                  onChangeActiveTab(InvestorDetailsTabsEnum.PACING, activeTab, {
                    backToTitle,
                    isGroup
                  });
                }}
              />
            )}
            {targetOCRatio !== null && (
              <NotificationWithProgressBar
                notificationTitle="Private Capital Target"
                notificationDescription={getNotificationDescription('overCommitmentRatio')}
                progressBarData={{
                  goal: detailsData?.progressBarValues?.capitalTarget || 0,
                  totalGoal: detailsData?.progressBarValues?.totalCapitalTarget || 0,
                  isGoalAchieved: detailsData?.progressBarValues?.isGoalAchieved || false,
                  total: detailsData?.progressBarValues?.economicExposure || 0
                }}
                onClickEditButton={openEditInvestorPage}
              />
            )}
          </>
        )}

      <MoreDetailsWithSimpleTable isMobile={isMobile}>
        <MoreDetails isSummaryTab={activeTab === InvestorDetailsTabsEnum.SUMMARY} isMobile={isMobile}>
          <MoreDetailsItems isMobile={isMobile} withBorderBottom={hasInfoTags}>
            {moreDetailsData.map(
              ({ name, showItem, value }) =>
                showItem && (
                  <MoreDetailsItem key={name} isMobile={isMobile}>
                    <MoreDetailsItemIconWrapper>
                      <ApprovedIcon width={23} height={23} fill={themeVar('font.weak')} />
                    </MoreDetailsItemIconWrapper>
                    <MoreDetailsItemTitle>{name}:</MoreDetailsItemTitle>
                    <MoreDetailsItemValue>{value}</MoreDetailsItemValue>
                  </MoreDetailsItem>
                )
            )}
            {entityType !== InvestorDetailsEntityTypeEnum.GroupedEntity &&
              !!detailsData?.sectorsToAvoid?.filter((sector: string) => sector !== '').length && (
                <MoreDetailsItem isMobile={isMobile}>
                  <SectorsToAvoidContainer>
                    <MoreDetailsItemIconWrapper>
                      <TerminatedIcon width={18} height={18} fill={themeVar('font.weak')} />
                    </MoreDetailsItemIconWrapper>
                    <MoreDetailsItemTitle>Sectors to Avoid:</MoreDetailsItemTitle>
                    {detailsData?.sectorsToAvoid?.map((sector: string, index: number) => (
                      <MoreDetailsItemValue key={`${sector}-${index}`}>
                        {sector.trim()}
                        {index < detailsData?.sectorsToAvoid.length - 1 && ','}
                      </MoreDetailsItemValue>
                    ))}
                  </SectorsToAvoidContainer>
                </MoreDetailsItem>
              )}
          </MoreDetailsItems>
          {hasInfoTags && (
            <InfoTagsItems>
              {infoTagsData.map(
                ({ name, showItem, value }) =>
                  showItem && (
                    <MoreDetailsItem key={name} isMobile={isMobile}>
                      <GreyTabItem>
                        <InfoIcon width={12} height={12} fill={themeVar('font.weak')} />
                        <MoreDetailsItemTitle>{name}:</MoreDetailsItemTitle>
                        <MoreDetailsItemValue>{value}</MoreDetailsItemValue>
                      </GreyTabItem>
                    </MoreDetailsItem>
                  )
              )}
            </InfoTagsItems>
          )}
        </MoreDetails>
        {entityType !== InvestorDetailsEntityTypeEnum.GroupedEntity && !ignoreAllocationTargets && activeTab !== InvestorDetailsTabsEnum.SUMMARY && (
          <SimpleTableWrapper isMobile={isMobile}>
            <SimpleTable assetClassTableData={assetClassTableData} hasCommitmentPlans={hasCommitmentPlans} targetOCRatio={targetOCRatio} />
          </SimpleTableWrapper>
        )}
      </MoreDetailsWithSimpleTable>
    </DetailsWrap>
  );
};

export default Details;

const DetailsWrap = styled(ContainerPage)<{ isMobile: boolean }>`
  padding: ${({ isMobile }) => (isMobile ? '10px 20px' : '10px 60px')};
  background-color: ${themeVar('layer.1')};
  border-bottom: 1px solid ${themeVar('border.base')};
`;

const CardsContainer = styled.div<{ isMobile: boolean }>`
  font-family: Blinker, serif;
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 10px;
  margin-bottom: 10px;
`;

const Card = styled.div<{ short?: boolean; isOpen?: boolean; isMobile: boolean }>`
  display: flex;
  cursor: ${({ short, isOpen }) => (short && isOpen ? 'pointer' : 'default')};
  width: ${({ short, isMobile }) => (isMobile ? '100%' : short ? '5.3%' : '30.9%')};
  height: 80px;
  min-height: 80px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: ${themeVar('layer.base')};
  border-radius: 10px;
`;

const CardTitle = styled.div`
  font-size: 23px;
  font-weight: 700;
  line-height: 32px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const CardDesc = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0;
  text-align: center;
  color: ${themeVar('font.weak')};
`;

const MoreDetailsWithSimpleTable = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 20px 30px;
  padding: 10px 0;
`;

const MoreDetails = styled.div<{ isSummaryTab: boolean; isMobile: boolean }>`
  width: ${({ isSummaryTab, isMobile }) => (isSummaryTab || isMobile ? '100%' : '45%')};
`;

const MoreDetailsItems = styled.div<{ isMobile: boolean; withBorderBottom?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 30px;
  padding: 15px 10px;
  border-bottom: ${({ withBorderBottom }) => (withBorderBottom ? `1px solid ${themeVar('border.base')}` : 'none')};
`;

const InfoTagsItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  padding: 15px 10px;
`;

const MoreDetailsItem = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
  font-size: 16px;
  line-height: 16px;
`;

const MoreDetailsItemIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MoreDetailsItemTitle = styled.div`
  font-weight: 400;
  color: ${themeVar('font.weak')};
`;

const MoreDetailsItemValue = styled.div`
  font-weight: 700;
  color: ${themeVar('font.strong')};
`;

const SectorsToAvoidContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const GreyTabItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  padding: 3px 10px;
  background: ${themeVar('layer.12')};
  font-size: 13px;
  font-weight: 600;
  line-height: 18.2px;
  color: ${themeVar('font.base')};
`;

const BiteSizeValueWrapper = styled.div`
  display: flex;
  flex-wrap: nowrap;
`;

const SimpleTableWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '100%' : '50%')};
`;
